import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, Minimize2 } from "lucide-react";
import { api } from "@/lib/api";

const AI_AVATAR = "https://static.prod-images.emergentagent.com/jobs/b272dcbc-055c-406b-9d98-d453af7b32ac/images/a1bfefffeba2259c9214212f624897c00a3d38c1bacdb05b06d7e6db4380d398.png";

const INTRO = "Hi, I'm the 555 Auto AI Concierge. What vehicle are you looking for today?";

export default function AIConcierge() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", content: INTRO }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      if (!sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble right now — please try again or call 1-555-AUTO-BRK." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 md:bottom-16 md:right-6 z-50 group"
          data-testid="ai-concierge-fab"
          aria-label="Open AI Concierge"
        >
          <div className="absolute inset-0 silver-gradient rounded-full blur-xl opacity-40 animate-pulse-slow" />
          <div className="relative w-16 h-16 rounded-full silver-gradient flex items-center justify-center shadow-2xl hover:scale-105 transition">
            <Sparkles className="w-6 h-6 text-[#050507]" strokeWidth={2.5} />
          </div>
          <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-emerald-500 text-[9px] font-bold text-white tracking-wider">AI</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-24 right-4 md:bottom-16 md:right-6 z-50 w-[min(380px,calc(100vw-2rem))] h-[min(580px,calc(100vh-9rem))] glass-strong rounded-2xl flex flex-col overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.6)] animate-fade-up"
          data-testid="ai-concierge-panel"
        >
          {/* header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 silver-gradient rounded-full blur-md opacity-50" />
                <img src={AI_AVATAR} alt="" className="relative w-10 h-10 rounded-full object-cover" />
              </div>
              <div>
                <div className="font-display text-sm">555 AI Concierge</div>
                <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  Online · Claude Sonnet
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-concierge-close" className="text-gray-400 hover:text-white p-1.5">
              <Minimize2 className="w-4 h-4" />
            </button>
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${
                    m.role === "user"
                      ? "silver-gradient text-[#050507] rounded-br-sm"
                      : "bg-white/5 border border-white/10 text-gray-100 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 px-3 py-2 rounded-2xl rounded-bl-sm flex gap-1">
                  <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                  <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
          </div>

          {/* suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5">
              {["BMW SUV under $700/mo", "Best EV lease right now", "I need a family vehicle"].map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 hover:bg-white/5 text-gray-300"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* input */}
          <div className="border-t border-white/10 p-3 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about a vehicle, budget, or deal…"
              className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-gray-500"
              data-testid="ai-concierge-input"
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              className="w-9 h-9 rounded-full silver-gradient flex items-center justify-center text-[#050507] disabled:opacity-40 hover:opacity-90 transition"
              data-testid="ai-concierge-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
